
import React from "react";
import { Mail, Crown, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogFooter } from "@/components/ui/dialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import EarlyAccessBenefits from "./EarlyAccessBenefits";

interface EarlyAccessFormProps {
  email: string;
  setEmail: (email: string) => void;
  isSubmitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

const EarlyAccessForm: React.FC<EarlyAccessFormProps> = ({
  email,
  setEmail,
  isSubmitting,
  onSubmit,
}) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  return (
    <form onSubmit={onSubmit} className="space-y-4 py-2">
      <div className="space-y-2">
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-propmatch-text-gray w-4 h-4 sm:w-5 sm:h-5" />
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("earlyAccess.form.emailPlaceholder")}
            className="pl-10 sm:pl-12 h-12 sm:h-14 bg-white/5 border-white/20 text-white placeholder:text-propmatch-text-gray focus:border-propmatch-blue rounded-lg text-sm sm:text-base"
            disabled={isSubmitting}
            required
            autoComplete="email"
          />
        </div>
        <p className="text-xs sm:text-sm text-propmatch-text-gray text-center px-1">
          🔒 {t("earlyAccess.form.securityMessage")}
        </p>
      </div>

      <EarlyAccessBenefits />

      {isSubmitting && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
            <p className="text-blue-400 font-semibold text-sm">{t("earlyAccess.form.processingMessage")}</p>
          </div>
        </div>
      )}

      <DialogFooter className="sm:justify-center">
        <Button
          type="submit"
          variant="gradient"
          size="xl"
          className="w-full transition-all h-12 sm:h-14 text-sm sm:text-base font-bold rounded-lg shadow-lg hover:shadow-xl"
          disabled={isSubmitting || !email}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {t("earlyAccess.form.submittingButton")}
            </>
          ) : (
            <>
              <Crown className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">{t("earlyAccess.form.submitButton")}</span>
              <span className="sm:hidden">{t("earlyAccess.form.submitButtonShort")}</span>
              <ArrowRight className="w-4 h-4 ml-1" />
            </>
          )}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default EarlyAccessForm;
